import { useRef, useState } from 'react';

import isEqual from 'lodash/fp/isEqual';
import { useGetInstancesQuery } from 'src/redux/api/connectInstance';
import { useGetMyProfileQuery } from 'src/redux/api/user';

import { getRdConnectInstancePing, RdConnectPingListParams } from '../api/automatedCapture';
import { RdConnectInstance, RdConnectInstancePingResponse } from '../types/apiContract';
import { usePingRequest } from './usePingRequest';
import { RDInstanceRequestStatus } from 'src/types/shared';
import { dispatch } from 'src/redux/store';
import { setPingCache } from 'src/redux/slices/app';

const checkMatchedDevices = (instance: RdConnectInstance | null, setInstance: (prop: RdConnectInstance) => void, execute: (params: any, options: any, instanceId?: number) => void, devices?: RdConnectInstance[], currentUserId?: string) => {
  if (!devices?.length) return;
  const matchedInstance = devices.find((device) =>
    instance ? device.id === instance.id : device.connectedUserId === currentUserId,
  ) || devices[0];

  setInstance(matchedInstance);
  execute({ rdConnectInstanceId: matchedInstance.id }, {}, matchedInstance.id);
};

export const useRdInstance = () => {
  const prevStateDevices = useRef<RdConnectInstance[] | null>(null);
  const [instance, setInstance] = useState<RdConnectInstance | null>(null);

  const { data: currentUser } = useGetMyProfileQuery();

  const { status, execute } = usePingRequest<RdConnectInstancePingResponse, RdConnectPingListParams>(
    getRdConnectInstancePing,
    {
      maxRetries: 3,
      retryDelay: 2000,
      initialParams: {},
    },
  );

  const { data: devices = [], isLoading, refetch, error } = useGetInstancesQuery();

  if (!isEqual(devices, prevStateDevices.current)) {
    prevStateDevices.current = devices;

    if (devices.length > 0) {
      checkMatchedDevices(instance, setInstance, execute, devices, currentUser?.id);
    }
  }

  const refetchInstances = async () => {
    const { data } = await refetch();
    dispatch(setPingCache({ instanceId: undefined, time: undefined, result: undefined }));
    if ((data?.length || 0) > 0) {
      checkMatchedDevices(instance, setInstance, execute, data, currentUser?.id);
    }
  };

  return {
    instance,
    error,
    status: !instance && !isLoading ? RDInstanceRequestStatus.ERROR : status,
    isLoading,
    refetchInstances,
  };
};
